import type React from "react";
import { lazy, Suspense } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { ScrollToTop } from "@/components";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import { Footer } from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import "./index.css";

// Lazy load page components for code splitting
const HomePage = lazy(() => import("@/pages/Home/HomePage"));
// Studenti pages (Phase 2 — ENABLED)
const StudentiPage = lazy(() => import("@/pages/Studenti/StudentiPage"));
const LiceoPage = lazy(() => import("@/pages/Studenti/LiceoPage"));
const UniversitaPage = lazy(() => import("@/pages/Studenti/UniversitaPage"));
const LaureatiMagistraliPage = lazy(() => import("@/pages/Studenti/LaureatiMagistraliPage"));
const DottoratoPage = lazy(() => import("@/pages/Studenti/DottoratoPage"));
const LaureatiPage = lazy(() => import("@/pages/Laureati/LaureatiPage"));
const NeoLaureatiPage = lazy(() => import("@/pages/Laureati/NeoLaureatiPage"));
const ConEsperienzaPage = lazy(() => import("@/pages/Laureati/ConEsperienzaPage"));
// Laureati sub-pages (Phase 2 — ENABLED)
const ProfessionistiPage = lazy(() => import("@/pages/Laureati/ProfessionistiPage"));
const RicercatoriPage = lazy(() => import("@/pages/Laureati/RicercatoriPage"));
const DocentiPage = lazy(() => import("@/pages/Laureati/DocentiPage"));
const DipendentiPubbliciPage = lazy(() => import("@/pages/Laureati/DipendentiPubbliciPage"));
// Enti pages (Phase 2 — ENABLED)
const EntiPage = lazy(() => import("@/pages/Enti/EntiPage"));
const TerzoSettorePage = lazy(() => import("@/pages/Enti/TerzoSettorePage"));
const AssociazioniStudenteschePage = lazy(
  () => import("@/pages/Enti/AssociazioniStudenteschePage")
);
const AssociazioniCulturaliPage = lazy(() => import("@/pages/Enti/AssociazioniCulturaliPage"));
const ComitatiPage = lazy(() => import("@/pages/Enti/ComitatiPage"));
const FondazioniPage = lazy(() => import("@/pages/Enti/FondazioniPage"));
// Opportunità page (Phase 2 — ENABLED)
const OpportunitaPage = lazy(() => import("@/pages/Opportunita/OpportunitaPage"));
const RisorsePage = lazy(() => import("@/pages/Risorse/RisorsePage"));
const ArticoloPage = lazy(() => import("@/pages/Risorse/ArticoloPage"));
const ContattiPage = lazy(() => import("@/pages/Support/ContattiPage"));
const ChiSiamoPage = lazy(() => import("@/pages/Support/ChiSiamoPage"));
const ComeFunzionaPage = lazy(() => import("@/pages/Support/ComeFunzionaPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFound/NotFoundPage"));

// Admin lazy imports
const AdminLoginPage = lazy(() => import("@/admin/pages/LoginPage"));
const AdminLayout = lazy(() => import("@/admin/components/AdminLayout"));
const AdminDashboardPage = lazy(() => import("@/admin/pages/DashboardPage"));
const AdminOpportunitaPage = lazy(() => import("@/admin/pages/OpportunitaPage"));
const AdminEventiPage = lazy(() => import("@/admin/pages/EventiPage"));
const AdminRisorsePage = lazy(() => import("@/admin/pages/RisorsePage"));
const AdminContattiPage = lazy(() => import("@/admin/pages/ContattiPage"));
const AdminNewsletterPage = lazy(() => import("@/admin/pages/NewsletterPage"));

// Auth Provider
import { AuthProvider } from "@/admin/context/AuthContext";

// Loading fallback
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      {/* biome-ignore lint/a11y/useSemanticElements: role="status" is the correct ARIA pattern for loading spinners */}
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)] mx-auto mb-4"
        role="status"
        aria-label="Caricamento in corso"
      />
      <p className="text-text-secondary">Caricamento...</p>
    </div>
  </div>
);

// Public site layout with Navigation + Footer
function PublicLayout() {
  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      <Navigation />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Admin Routes - no Navigation/Footer */}
            <Route
              path="/admin/login"
              element={
                <AuthProvider>
                  <AdminLoginPage />
                </AuthProvider>
              }
            />
            <Route
              path="/admin"
              element={
                <AuthProvider>
                  <AdminLayout />
                </AuthProvider>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="opportunita" element={<AdminOpportunitaPage />} />
              <Route path="eventi" element={<AdminEventiPage />} />
              <Route path="risorse" element={<AdminRisorsePage />} />
              <Route path="contatti" element={<AdminContattiPage />} />
              <Route path="newsletter" element={<AdminNewsletterPage />} />
            </Route>

            {/* Public Routes - with Navigation/Footer */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              {/* Studenti */}
              <Route path="/studenti" element={<StudentiPage />} />
              <Route path="/studenti/liceo" element={<LiceoPage />} />
              <Route path="/studenti/universita" element={<UniversitaPage />} />
              <Route path="/studenti/laureati-magistrali" element={<LaureatiMagistraliPage />} />
              <Route path="/studenti/dottorato" element={<DottoratoPage />} />
              {/* Laureati */}
              <Route path="/laureati" element={<LaureatiPage />} />
              <Route path="/laureati/neo-laureati" element={<NeoLaureatiPage />} />
              <Route path="/laureati/con-esperienza" element={<ConEsperienzaPage />} />
              <Route path="/laureati/professionisti" element={<ProfessionistiPage />} />
              <Route path="/laureati/ricercatori" element={<RicercatoriPage />} />
              <Route path="/laureati/docenti" element={<DocentiPage />} />
              <Route path="/laureati/dipendenti-pubblici" element={<DipendentiPubbliciPage />} />
              {/* Enti */}
              <Route path="/enti" element={<EntiPage />} />
              <Route path="/enti/terzo-settore" element={<TerzoSettorePage />} />
              <Route path="/enti/associazioni-studentesche" element={<AssociazioniStudenteschePage />} />
              <Route path="/enti/associazioni-culturali" element={<AssociazioniCulturaliPage />} />
              <Route path="/enti/comitati" element={<ComitatiPage />} />
              <Route path="/enti/fondazioni" element={<FondazioniPage />} />
              {/* Opportunità */}
              <Route path="/opportunita" element={<OpportunitaPage />} />
              {/* Risorse, Contatti, Chi Siamo, Come Funziona */}
              <Route path="/risorse" element={<RisorsePage />} />
              <Route path="/risorse/:slug" element={<ArticoloPage />} />
              <Route path="/contatti" element={<ContattiPage />} />
              <Route path="/chi-siamo" element={<ChiSiamoPage />} />
              <Route path="/come-funziona" element={<ComeFunzionaPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
